"use client";

import { useBooleanState } from "@nvon/react-toolbox";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";

import { useMessages } from "@/lib/i18n/useMessages";

import { getInputs, Input } from "./getInputs";

const faktorUntergrenze = 0.25;
const faktorObergrenze = 2.5;

export function useAbfindungWizardState() {
  const messages = useMessages();
  const abfindung = messages.calculator.abfindung;
  const theme = useTheme();

  const { state: isCalculatorStarted, on: startCalculator } =
    useBooleanState(false);

  const [counter, setCounter] = useState(0);
  const increaseCounter = (index: number) => {
    if (index + 1 > counter) {
      setCounter(index + 1);
    }
  };

  const [isMoreThan10, setIsMoreThan10] = useState<boolean | undefined>(
    undefined,
  );

  const handleIsMoreThan10 = (value: boolean | undefined) => {
    setIsMoreThan10(value);
    increaseCounter(0);
  };

  const [monthlyIncome, setMonthlyIncome] = useState(0);

  const handleChangeMonthlyIncome = (value: number) => {
    setMonthlyIncome(value);
  };

  const { state: isMonthlyIncomeConfirmed, on: confirmMonthlyIncome } =
    useBooleanState(false);

  const handleConfirmMonthlyIncome = () => {
    confirmMonthlyIncome();
    increaseCounter(1);
  };

  const [whenStartedWorking, setWhenStartedWorking] = useState<
    Date | undefined
  >(undefined);

  const handleWhenStartedWorking = (value: Date | undefined) => {
    setWhenStartedWorking(value);
  };

  const { state: isCalculationStarted, on: setIsCalculationStartedTrue } =
    useBooleanState(false);

  const handleIsCalculationStarted = () => {
    setIsCalculationStartedTrue();
    increaseCounter(2);
  };

  const beschäftigungsdauerInJahren = useMemo(() => {
    if (!whenStartedWorking) return 0;
    return Math.floor(dayjs().diff(dayjs(whenStartedWorking), "year"));
  }, [whenStartedWorking]);

  const untergrenze = (
    beschäftigungsdauerInJahren *
    faktorUntergrenze *
    monthlyIncome
  ).toFixed(2);

  const obergrenze = (
    beschäftigungsdauerInJahren *
    faktorObergrenze *
    monthlyIncome
  ).toFixed(2);

  const inputs: Input[] = getInputs({
    theme,
    abfindung,
    isMoreThan10,
    handleIsMoreThan10,
    monthlyIncome,
    handleChangeMonthlyIncome,
    isMonthlyIncomeConfirmed,
    handleConfirmMonthlyIncome,
    whenStartedWorking,
    handleWhenStartedWorking,
    handleIsCalculationStarted,
    beschäftigungszeit: beschäftigungsdauerInJahren,
  });

  return {
    theme,
    abfindung,
    isCalculatorStarted,
    startCalculator,
    counter,
    inputs,
    isMoreThan10,
    isMonthlyIncomeConfirmed,
    isCalculationStarted,
    monthlyIncome,
    beschäftigungsdauerInJahren,
    untergrenze,
    obergrenze,
  };
}
